import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Form, Spinner } from "react-bootstrap";
import { currentBrowser } from "../utils/browserInfo";

// Who accepted and where they were, asked for every time a hotel accepts - a booking by link or
// from the portal, or a room hold. Kept as proof so neither side can later say the other accepted.
// The server refuses an acceptance without both, so this form is the only way through.

const NAME_KEY = "acceptanceProofName";

const rememberedName = () => {
  try {
    return localStorage.getItem(NAME_KEY) || "";
  } catch {
    return "";
  }
};

const rememberName = (name) => {
  try {
    localStorage.setItem(NAME_KEY, name);
  } catch {
    // Private windows can refuse storage; the name is just typed again next time.
  }
};

// The name typed so far, carried across the reload that Try again may do.
const DRAFT_NAME_KEY = "acceptanceProofDraftName";

const takeDraftName = () => {
  try {
    const draft = sessionStorage.getItem(DRAFT_NAME_KEY);
    sessionStorage.removeItem(DRAFT_NAME_KEY);
    return draft;
  } catch {
    return null;
  }
};

const keepDraftName = (name) => {
  try {
    sessionStorage.setItem(DRAFT_NAME_KEY, name);
  } catch {
    // The name is typed again after the reload.
  }
};

const HELP_PHONE = "9611483255";
const HELP_LANG_KEY = "acceptanceProofHelpLang";

const rememberedHelpLang = () => {
  try {
    return localStorage.getItem(HELP_LANG_KEY) || "en";
  } catch {
    return "en";
  }
};

// Hotel staff are often not comfortable in English, so the steps come in their language too.
// Words they will see on screen (Location, Allow, Try again, Settings) stay in English.
const HELP_LANGS = [
  ["en", "English"],
  ["te", "తెలుగు"],
  ["ta", "தமிழ்"],
  ["kn", "ಕನ್ನಡ"],
  ["hi", "हिंदी"],
];

const HELP_TEXT = {
  en: {
    off: {
      title: "Your phone's Location is OFF",
      android: [
        "Swipe down from the top of the screen.",
        "Tap the Location icon to turn it ON.",
        "Come back here and tap Try again. If asked, tap Allow.",
      ],
      // An iPhone gives the same refusal when Location Services, the browser's own location or
      // the site is off, so the title does not claim the phone is off. {app} is "Safari Websites"
      // in Safari, else the browser's name in that list.
      iphoneTitle: "We could not get your location",
      iphone: [
        "Open Settings → Privacy & Security → Location Services and turn it ON.",
        "On the same page, scroll down, tap {app} and choose While Using the App.",
        "Come back here and tap Try again. If asked, tap Allow.",
      ],
    },
    blocked: {
      title: "Location already ON? Then allow it for this website:",
      android: [
        "Tap the small icon to the left of the web address at the top.",
        "Tap Permissions, then turn the Location switch ON.",
        "Tap Try again.",
      ],
      iphone: [
        "Tap aA next to the web address.",
        "Tap Website Settings → Location → Allow.",
        "Tap Try again.",
      ],
    },
    timeout: {
      title: "Could not find your location",
      text: "Check that Location is ON. Move near a window or step outside, then tap Try again.",
    },
    unsupported: {
      title: "This browser cannot share location",
      text: "Please open this link in {browser}.",
    },
    // A link opened inside an app (Instagram, Gmail...) uses that app's browser; send them out of it.
    inApp: {
      title: "This app cannot share your location",
      steps: [
        "Tap ••• or ⋮ at the top or bottom of this screen.",
        "Tap Open in {browser} (or Open in browser).",
        "Continue there, and tap Allow if asked.",
      ],
    },
    copyLink: "Or copy this link and open it in {browser}.",
    help: "Still not working? Call us",
  },
  te: {
    off: {
      title: "మీ ఫోన్‌లో Location OFF లో ఉంది",
      android: [
        "స్క్రీన్ పై నుండి కిందికి లాగండి (swipe down).",
        "Location ఐకాన్‌ను నొక్కి ON చేయండి.",
        "ఇక్కడికి తిరిగి వచ్చి Try again నొక్కండి. అడిగితే Allow నొక్కండి.",
      ],
      iphoneTitle: "మీ లొకేషన్ తీసుకోలేకపోయాము",
      iphone: [
        "Settings → Privacy & Security → Location Services తెరిచి ON చేయండి.",
        "అదే పేజీలో కిందికి స్క్రోల్ చేసి {app} నొక్కి While Using the App ఎంచుకోండి.",
        "ఇక్కడికి తిరిగి వచ్చి Try again నొక్కండి. అడిగితే Allow నొక్కండి.",
      ],
    },
    blocked: {
      title: "Location ఇప్పటికే ON లో ఉందా? అయితే ఈ వెబ్‌సైట్‌కు అనుమతి ఇవ్వండి:",
      android: [
        "పైన ఉన్న వెబ్ అడ్రస్‌కు ఎడమవైపు ఉన్న చిన్న ఐకాన్‌ను నొక్కండి.",
        "Permissions నొక్కి, Location స్విచ్‌ను ON చేయండి.",
        "Try again నొక్కండి.",
      ],
      iphone: [
        "వెబ్ అడ్రస్ పక్కన ఉన్న aA ను నొక్కండి.",
        "Website Settings → Location → Allow ఎంచుకోండి.",
        "Try again నొక్కండి.",
      ],
    },
    timeout: {
      title: "మీ లొకేషన్ దొరకలేదు",
      text: "Location ON లో ఉందో లేదో చూడండి. కిటికీ దగ్గరకు లేదా బయటకు వెళ్లి Try again నొక్కండి.",
    },
    unsupported: {
      title: "ఈ బ్రౌజర్‌లో Location పనిచేయదు",
      text: "ఈ లింక్‌ను {browser} లో తెరవండి.",
    },
    inApp: {
      title: "ఈ యాప్‌లో Location పనిచేయదు",
      steps: [
        "ఈ స్క్రీన్‌పై ఉన్న ••• లేదా ⋮ నొక్కండి.",
        "Open in {browser} (లేదా Open in browser) నొక్కండి.",
        "అక్కడ కొనసాగించండి. అడిగితే Allow నొక్కండి.",
      ],
    },
    copyLink: "లేదా ఈ లింక్‌ను కాపీ చేసి {browser} లో తెరవండి.",
    help: "ఇంకా పని చేయడం లేదా? మాకు కాల్ చేయండి",
  },
  ta: {
    off: {
      title: "உங்கள் போனில் Location OFF ஆக உள்ளது",
      android: [
        "திரையின் மேலிருந்து கீழே இழுக்கவும் (swipe down).",
        "Location ஐகானைத் தொட்டு ON செய்யவும்.",
        "இங்கே திரும்பி வந்து Try again அழுத்தவும். கேட்டால் Allow அழுத்தவும்.",
      ],
      iphoneTitle: "உங்கள் இருப்பிடத்தைப் பெற முடியவில்லை",
      iphone: [
        "Settings → Privacy & Security → Location Services திறந்து ON செய்யவும்.",
        "அதே பக்கத்தில் கீழே ஸ்க்ரோல் செய்து {app} தொட்டு While Using the App தேர்ந்தெடுக்கவும்.",
        "இங்கே திரும்பி வந்து Try again அழுத்தவும். கேட்டால் Allow அழுத்தவும்.",
      ],
    },
    blocked: {
      title: "Location ஏற்கனவே ON ஆக உள்ளதா? அப்படியென்றால் இந்த இணையதளத்துக்கு அனுமதி கொடுக்கவும்:",
      android: [
        "மேலே உள்ள இணைய முகவரிக்கு இடதுபுறம் உள்ள சிறிய ஐகானைத் தொடவும்.",
        "Permissions தொட்டு, Location சுவிட்சை ON செய்யவும்.",
        "Try again அழுத்தவும்.",
      ],
      iphone: [
        "இணைய முகவரிக்கு அருகில் உள்ள aA ஐத் தொடவும்.",
        "Website Settings → Location → Allow தேர்ந்தெடுக்கவும்.",
        "Try again அழுத்தவும்.",
      ],
    },
    timeout: {
      title: "உங்கள் இருப்பிடம் கிடைக்கவில்லை",
      text: "Location ON ஆக உள்ளதா என பார்க்கவும். ஜன்னல் அருகே அல்லது வெளியே சென்று Try again அழுத்தவும்.",
    },
    unsupported: {
      title: "இந்த பிரவுசரில் Location வேலை செய்யாது",
      text: "இந்த லிங்கை {browser}-ல் திறக்கவும்.",
    },
    inApp: {
      title: "இந்த ஆப்பில் Location வேலை செய்யாது",
      steps: [
        "இந்தத் திரையில் உள்ள ••• அல்லது ⋮ ஐத் தொடவும்.",
        "Open in {browser} (அல்லது Open in browser) தொடவும்.",
        "அங்கே தொடரவும். கேட்டால் Allow அழுத்தவும்.",
      ],
    },
    copyLink: "அல்லது இந்த லிங்கை காப்பி செய்து {browser}-ல் திறக்கவும்.",
    help: "இன்னும் வேலை செய்யவில்லையா? எங்களை அழைக்கவும்",
  },
  kn: {
    off: {
      title: "ನಿಮ್ಮ ಫೋನ್‌ನಲ್ಲಿ Location OFF ಆಗಿದೆ",
      android: [
        "ಸ್ಕ್ರೀನ್‌ನ ಮೇಲಿನಿಂದ ಕೆಳಗೆ ಎಳೆಯಿರಿ (swipe down).",
        "Location ಐಕಾನ್ ಒತ್ತಿ ON ಮಾಡಿ.",
        "ಇಲ್ಲಿಗೆ ಹಿಂತಿರುಗಿ Try again ಒತ್ತಿರಿ. ಕೇಳಿದರೆ Allow ಒತ್ತಿರಿ.",
      ],
      iphoneTitle: "ನಿಮ್ಮ ಸ್ಥಳವನ್ನು ಪಡೆಯಲು ಆಗಲಿಲ್ಲ",
      iphone: [
        "Settings → Privacy & Security → Location Services ತೆರೆದು ON ಮಾಡಿ.",
        "ಅದೇ ಪುಟದಲ್ಲಿ ಕೆಳಗೆ ಸ್ಕ್ರೋಲ್ ಮಾಡಿ {app} ಒತ್ತಿ While Using the App ಆಯ್ಕೆ ಮಾಡಿ.",
        "ಇಲ್ಲಿಗೆ ಹಿಂತಿರುಗಿ Try again ಒತ್ತಿರಿ. ಕೇಳಿದರೆ Allow ಒತ್ತಿರಿ.",
      ],
    },
    blocked: {
      title: "Location ಈಗಾಗಲೇ ON ಆಗಿದೆಯೇ? ಹಾಗಾದರೆ ಈ ವೆಬ್‌ಸೈಟ್‌ಗೆ ಅನುಮತಿ ನೀಡಿ:",
      android: [
        "ಮೇಲಿರುವ ವೆಬ್ ವಿಳಾಸದ ಎಡಭಾಗದ ಸಣ್ಣ ಐಕಾನ್ ಒತ್ತಿರಿ.",
        "Permissions ಒತ್ತಿ, Location ಸ್ವಿಚ್ ON ಮಾಡಿ.",
        "Try again ಒತ್ತಿರಿ.",
      ],
      iphone: [
        "ವೆಬ್ ವಿಳಾಸದ ಪಕ್ಕದ aA ಒತ್ತಿರಿ.",
        "Website Settings → Location → Allow ಆಯ್ಕೆ ಮಾಡಿ.",
        "Try again ಒತ್ತಿರಿ.",
      ],
    },
    timeout: {
      title: "ನಿಮ್ಮ ಸ್ಥಳ ಸಿಗಲಿಲ್ಲ",
      text: "Location ON ಆಗಿದೆಯೇ ನೋಡಿ. ಕಿಟಕಿಯ ಹತ್ತಿರ ಅಥವಾ ಹೊರಗೆ ಹೋಗಿ Try again ಒತ್ತಿರಿ.",
    },
    unsupported: {
      title: "ಈ ಬ್ರೌಸರ್‌ನಲ್ಲಿ Location ಕೆಲಸ ಮಾಡುವುದಿಲ್ಲ",
      text: "ಈ ಲಿಂಕ್ ಅನ್ನು {browser}‌ನಲ್ಲಿ ತೆರೆಯಿರಿ.",
    },
    inApp: {
      title: "ಈ ಆ್ಯಪ್‌ನಲ್ಲಿ Location ಕೆಲಸ ಮಾಡುವುದಿಲ್ಲ",
      steps: [
        "ಈ ಸ್ಕ್ರೀನ್‌ನಲ್ಲಿರುವ ••• ಅಥವಾ ⋮ ಒತ್ತಿರಿ.",
        "Open in {browser} (ಅಥವಾ Open in browser) ಒತ್ತಿರಿ.",
        "ಅಲ್ಲಿ ಮುಂದುವರಿಸಿ. ಕೇಳಿದರೆ Allow ಒತ್ತಿರಿ.",
      ],
    },
    copyLink: "ಅಥವಾ ಈ ಲಿಂಕ್ ಕಾಪಿ ಮಾಡಿ {browser}‌ನಲ್ಲಿ ತೆರೆಯಿರಿ.",
    help: "ಇನ್ನೂ ಆಗುತ್ತಿಲ್ಲವೇ? ನಮಗೆ ಕರೆ ಮಾಡಿ",
  },
  hi: {
    off: {
      title: "आपके फ़ोन की Location OFF है",
      android: [
        "स्क्रीन के ऊपर से नीचे की ओर स्वाइप करें।",
        "Location आइकन दबाकर उसे ON करें।",
        "यहाँ वापस आकर Try again दबाएँ। पूछे तो Allow दबाएँ।",
      ],
      iphoneTitle: "हम आपकी लोकेशन नहीं ले पाए",
      iphone: [
        "Settings → Privacy & Security → Location Services खोलें और ON करें।",
        "उसी पेज पर नीचे स्क्रॉल करें, {app} दबाएँ और While Using the App चुनें।",
        "यहाँ वापस आकर Try again दबाएँ। पूछे तो Allow दबाएँ।",
      ],
    },
    blocked: {
      title: "Location पहले से ON है? तो इस वेबसाइट को अनुमति दें:",
      android: [
        "ऊपर वेब पते के बाईं ओर वाले छोटे आइकन को दबाएँ।",
        "Permissions दबाएँ, फिर Location का स्विच ON करें।",
        "Try again दबाएँ।",
      ],
      iphone: [
        "वेब पते के पास वाले aA को दबाएँ।",
        "Website Settings → Location → Allow चुनें।",
        "Try again दबाएँ।",
      ],
    },
    timeout: {
      title: "आपकी लोकेशन नहीं मिल पाई",
      text: "देखें कि Location ON है। खिड़की के पास या बाहर जाकर Try again दबाएँ।",
    },
    unsupported: {
      title: "इस ब्राउज़र में Location काम नहीं करती",
      text: "यह लिंक {browser} में खोलें।",
    },
    inApp: {
      title: "इस ऐप में Location काम नहीं करती",
      steps: [
        "इस स्क्रीन पर ••• या ⋮ दबाएँ।",
        "Open in {browser} (या Open in browser) दबाएँ।",
        "वहीं आगे बढ़ें। पूछे तो Allow दबाएँ।",
      ],
    },
    copyLink: "या यह लिंक कॉपी करके {browser} में खोलें।",
    help: "फिर भी नहीं हो रहा? हमें कॉल करें",
  },
};

// The pictures are real phone screenshots with the place to tap ringed in red - people found the
// right button from a real screenshot far more easily than from a drawing.
function HelpShot({ src, alt, width, height, caption }) {
  return (
    <div className="mb-2">
      {caption && <div className="fw-bold small mb-1" style={{ color: "#dc3545" }}>{caption}</div>}
      <img src={`/img/location-help/${src}`} alt={alt} width={width} height={height} className="d-block w-100 h-auto rounded" />
    </div>
  );
}

/** The quick-settings panel pulled down, with the Location tile ringed. */
function SwipeDownPicture() {
  return (
    <div className="my-2 mx-auto" style={{ maxWidth: 300 }}>
      <HelpShot
        src="quick-settings-location.png"
        alt="Quick settings panel with the Location button circled in red"
        width="698"
        height="317"
        caption="↓ Swipe down from the top, tap Location"
      />
    </div>
  );
}

/** Chrome's address-bar icon, then Permissions, then the Location switch - each ringed in turn. */
function SitePermissionPicture() {
  return (
    <div className="my-2 mx-auto" style={{ maxWidth: 300 }}>
      <HelpShot
        src="chrome-site-settings.png"
        alt="Chrome's top bar with the small icon left of the web address circled in red"
        width="738"
        height="114"
        caption="1. Tap this icon at the top"
      />
      <HelpShot
        src="chrome-permissions-row.png"
        alt="The menu that opens, with Permissions circled in red"
        width="738"
        height="448"
        caption="2. Tap Permissions"
      />
      <HelpShot
        src="chrome-location-switch.png"
        alt="The Permissions page, with the Location switch circled in red"
        width="738"
        height="464"
        caption="3. Turn the Location switch ON"
      />
    </div>
  );
}

/** A "Copy link" button for moving to another browser; shows the address to copy by hand if that fails. */
function CopyLinkLine({ text }) {
  const [copyState, setCopyState] = useState("idle"); // idle | copied | manual
  const href = typeof window === "undefined" ? "" : window.location.href;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(href);
      setCopyState("copied");
    } catch {
      setCopyState("manual");
    }
  };
  return (
    <div className="border-top pt-2 mb-2">
      <div className="fw-semibold mb-1">{text}</div>
      {copyState === "manual" ? (
        <Form.Control size="sm" readOnly value={href} onFocus={(event) => event.target.select()} />
      ) : (
        <Button variant="outline-dark" size="sm" onClick={copy}>
          <i className={`bi ${copyState === "copied" ? "bi-check2" : "bi-clipboard"} me-1`} aria-hidden="true" />
          {copyState === "copied" ? "Link copied" : "Copy link"}
        </Button>
      )}
    </div>
  );
}

/**
 * What went wrong with the location and how to fix it, in the reader's language, with our number.
 * A page that already has its own language picker passes `fixedLang` and the picker here is hidden.
 */
function LocationHelp({ locationState, disabled, onRetry, fixedLang }) {
  const [chosenLang, setLang] = useState(rememberedHelpLang);
  const lang = fixedLang || chosenLang;
  const chooseLang = (code) => {
    setLang(code);
    try {
      localStorage.setItem(HELP_LANG_KEY, code);
    } catch {
      // Falls back to English next time.
    }
  };
  const text = HELP_TEXT[lang] || HELP_TEXT.en;
  const device = currentBrowser();
  const iphone = device.ios;
  // Where to send them when this browser cannot help: the phone's own browser.
  const homeBrowser = iphone ? "Safari" : "Chrome";
  const fill = (line) => line
    .replace(/\{browser\}/g, homeBrowser)
    .replace(/\{app\}/g, device.browser === "Safari" ? "Safari Websites" : device.browser);
  // An app's built-in browser is the problem whatever it reported, unless it was just slow.
  const inApp = device.inApp && locationState !== "timeout";
  const problem = inApp ? text.inApp : text[locationState];
  const title = !inApp && iphone && locationState === "off" ? text.off.iphoneTitle : problem.title;
  const stepsFor = (section) => (iphone ? section.iphone : section.android);
  const steps = inApp ? problem.steps : problem.android ? stepsFor(problem) : null;
  // Only Safari has the "aA" site settings; other iPhone browsers are sent to Safari instead.
  const showSiteSettings = locationState === "off" && !inApp && (!iphone || device.browser === "Safari");
  const showCopyLink = inApp || locationState === "unsupported" || (iphone && locationState === "off" && device.browser !== "Safari");

  return (
    <Alert variant={locationState === "off" ? "danger" : "warning"} className="py-2 mb-2 acceptance-location-help">
      {!fixedLang && (
        <div className="d-flex flex-wrap gap-1 mb-2">
          {HELP_LANGS.map(([code, label]) => (
            <Button
              key={code}
              size="sm"
              variant={code === lang ? "dark" : "outline-dark"}
              className="py-0 px-2"
              onClick={() => chooseLang(code)}
            >
              {label}
            </Button>
          ))}
        </div>
      )}
      <div className="fw-bold mb-1">{title}</div>
      {locationState === "off" && !iphone && !inApp && <SwipeDownPicture />}
      {steps ? (
        <ol className="ps-3 mb-2">
          {steps.map((step) => <li key={step}>{fill(step)}</li>)}
        </ol>
      ) : (
        <div className="mb-2">{fill(problem.text)}</div>
      )}
      {locationState !== "unsupported" && (
        <Button variant="dark" className="mb-2" disabled={disabled} onClick={onRetry}>
          Try again
        </Button>
      )}
      {/* The phone being off is far more common, so it comes first; a blocked site is the fallback. */}
      {showSiteSettings && (
        <div className="border-top pt-2 mb-2">
          <div className="fw-semibold mb-1">{text.blocked.title}</div>
          {!iphone && <SitePermissionPicture />}
          <ol className="ps-3 mb-0">
            {stepsFor(text.blocked).map((step) => <li key={step}>{step}</li>)}
          </ol>
        </div>
      )}
      {showCopyLink && <CopyLinkLine text={fill(text.copyLink)} />}
      <div className="small fw-semibold">{text.help}</div>
      <Button variant="success" size="sm" className="mt-1" href={`tel:+91${HELP_PHONE}`}>
        <i className="bi bi-telephone-fill me-1" aria-hidden="true" />
        Call {HELP_PHONE.slice(0, 5)} {HELP_PHONE.slice(5)}
      </Button>
    </Alert>
  );
}

const GEO_REASONS = { 1: "DENIED", 2: "UNAVAILABLE", 3: "TIMEOUT" };

// The browser's own setting for location on this site (granted, denied or prompt), added to a
// failure report to help tell a blocked site from a phone or browser that is off.
const permissionState = async () => {
  try {
    const status = await navigator.permissions?.query({ name: "geolocation" });
    return status?.state || "";
  } catch {
    return "";
  }
};

/**
 * Collects the name and a fresh location reading while `active` (the dialog or page is open).
 * `proof` is the request body to send, or null until both are in hand. `onLocationFailure`, when
 * given, is told about every failed attempt as { reason, message } so the office can see why.
 */
export function useAcceptanceProof(active, onLocationFailure) {
  const [name, setName] = useState(() => takeDraftName() ?? rememberedName());
  const [location, setLocation] = useState(null);
  // idle | locating | ready | off | timeout | unsupported
  const [locationState, setLocationState] = useState("idle");
  const nameRef = useRef(name);
  nameRef.current = name;
  const failureRef = useRef(onLocationFailure);
  failureRef.current = onLocationFailure;

  // Waits at most 1.5 s, so a reload after Try again is not held up by a slow network.
  const reportFailure = useCallback(async (reason, message) => {
    const report = failureRef.current;
    if (!report) return;
    const send = (async () => {
      const permission = await permissionState();
      const detail = [message, permission && `permission ${permission}`].filter(Boolean).join(" · ");
      await report({ reason, message: detail.slice(0, 200) });
    })();
    try {
      await Promise.race([send, new Promise((resolve) => setTimeout(resolve, 1500))]);
    } catch {
      // A report that cannot be sent never gets in the way of accepting.
    }
  }, []);

  // `reloadIfStuck` is for the Try again button. Once Chrome has refused location on a page, it
  // keeps refusing on that page even after the phone's Location is switched on - only a reload
  // clears it. So a Try again that still fails reloads the page, keeping the name typed so far.
  const requestLocation = useCallback((reloadIfStuck = false) => {
    if (typeof navigator === "undefined" || !navigator.geolocation || !window.isSecureContext) {
      setLocationState("unsupported");
      reportFailure("UNSUPPORTED", window.isSecureContext ? "No location in this browser" : "Not a secure page");
      return;
    }
    setLocation(null);
    setLocationState("locating");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracyMeters: Number.isFinite(position.coords.accuracy) ? Math.round(position.coords.accuracy) : null,
        });
        setLocationState("ready");
      },
      // Chrome reports the phone's Location switch being off as "permission denied" - even its own
      // permission check says denied while the site is listed as Allowed - so a switched-off phone
      // and a blocked site cannot be told apart. Both get the "off" help, which covers each in turn.
      (error) => {
        const reported = reportFailure(GEO_REASONS[error?.code] || "UNKNOWN", error?.message);
        if (error?.code === 3) {
          setLocationState("timeout");
        } else if (reloadIfStuck) {
          keepDraftName(nameRef.current);
          reported.finally(() => window.location.reload());
        } else {
          setLocationState("off");
        }
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 },
    );
  }, [reportFailure]);

  // A fresh reading every time the form opens, so an old position is never reused.
  useEffect(() => {
    if (active) {
      requestLocation();
    } else {
      setLocation(null);
      setLocationState("idle");
    }
  }, [active, requestLocation]);

  // Turning Location on usually means leaving for Settings; try again by itself on the way back.
  const failed = active && ["off", "timeout"].includes(locationState);
  useEffect(() => {
    if (!failed) return undefined;
    const retryWhenBack = () => {
      if (document.visibilityState === "visible") requestLocation();
    };
    document.addEventListener("visibilitychange", retryWhenBack);
    return () => document.removeEventListener("visibilitychange", retryWhenBack);
  }, [failed, requestLocation]);

  const trimmed = name.trim().replace(/\s+/g, " ");
  const nameOk = trimmed.length >= 2 && /\p{L}/u.test(trimmed);
  const proof = nameOk && location ? { name: trimmed, ...location } : null;

  const markUsed = useCallback(() => {
    if (nameOk) rememberName(trimmed);
  }, [nameOk, trimmed]);

  return { name, setName, nameOk, locationState, requestLocation, proof, markUsed };
}

const FIELD_LABELS = {
  name: "Your name",
  namePlaceholder: "Enter your full name",
  location: "Your location",
  locating: "Getting your location...",
  located: "Location captured",
  proofNote: "Your name, location and device details will be saved as proof of this acceptance.",
};

/**
 * The name box, the location status and the consent line. `labels` overrides any of FIELD_LABELS,
 * and `lang` (en, te, kn, ta, hi) fixes the location help to that language - both for a page that
 * shows everything in the reader's own language.
 */
export function AcceptanceProofFields({ state, disabled = false, labels, lang }) {
  const { name, setName, locationState, requestLocation } = state;
  const text = { ...FIELD_LABELS, ...labels };
  return (
    <div className="acceptance-proof">
      <Form.Group className="mb-3" controlId="acceptance-proof-name">
        <Form.Label className="fw-semibold">{text.name}</Form.Label>
        <Form.Control
          value={name}
          maxLength={80}
          autoComplete="name"
          placeholder={text.namePlaceholder}
          disabled={disabled}
          onChange={(event) => setName(event.target.value)}
        />
      </Form.Group>

      <div className="fw-semibold mb-1">{text.location}</div>
      {locationState === "locating" && (
        <div className="d-flex align-items-center gap-2 mb-2 acceptance-proof-note">
          <Spinner animation="border" size="sm" /> {text.locating}
        </div>
      )}
      {locationState === "ready" && (
        <div className="text-success fw-semibold mb-2">
          <i className="bi bi-geo-alt-fill me-1" aria-hidden="true" />
          {text.located}
        </div>
      )}
      {["off", "timeout", "unsupported"].includes(locationState) && (
        <LocationHelp
          locationState={locationState}
          disabled={disabled}
          fixedLang={lang && HELP_TEXT[lang] ? lang : undefined}
          onRetry={() => requestLocation(true)}
        />
      )}

      <div className="small acceptance-proof-note mt-2">
        {text.proofNote}
      </div>
    </div>
  );
}
